import { useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import DOMPurify from "dompurify";
import { BulletListIcon, OrderedListIcon } from "components/atoms/Icon";
import styles from "./AuditMarkdownField.module.scss";

interface AuditMarkdownFieldProps {
    label: string;
    value: string | null | undefined;
    defaultContent?: string;
    onSave: (html: string | null) => void;
    isSaving?: boolean;
}

function AuditMarkdownField({ label, value, defaultContent, onSave, isSaving }: AuditMarkdownFieldProps) {
    const [editing, setEditing] = useState(false);

    const editor = useEditor({
        extensions: [StarterKit],
        content: value || defaultContent || "",
        editorProps: {
            attributes: { class: styles.editorContent },
        },
    });

    const handleSave = () => {
        if (!editor) return;
        onSave(editor.isEmpty ? null : editor.getHTML());
        setEditing(false);
    };

    const toolbarBtn = (
        btnLabel: React.ReactNode,
        _active: boolean,
        onClick: () => void,
        className = styles.toolbarButton
    ) => (
        <button type="button" onClick={onClick} className={className}>
            {btnLabel}
        </button>
    );

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <h3 className={styles.label}>{label}</h3>
                {/* Hidden in print */}
                <span className="audit-edit-controls">
                    {editing ? (
                        <>
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isSaving}
                                className={styles.primaryButton}
                            >
                                {isSaving ? "Saving…" : "Save"}
                            </button>
                            <button type="button" onClick={() => setEditing(false)} className={styles.ghostButton}>
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button type="button" onClick={() => setEditing(true)} className={styles.ghostButton}>
                            Edit
                        </button>
                    )}
                </span>
            </div>

            {/* Edit mode — hidden in print */}
            {editing && (
                <div className={`audit-edit-controls ${styles.editorContainer}`}>
                    {editor && (
                        <div className={styles.toolbar}>
                            {toolbarBtn(
                                "B",
                                editor.isActive("bold"),
                                () => editor.chain().focus().toggleBold().run(),
                                styles.toolbarButtonBold
                            )}
                            {toolbarBtn(
                                "I",
                                editor.isActive("italic"),
                                () => editor.chain().focus().toggleItalic().run(),
                                styles.toolbarButtonItalic
                            )}
                            {toolbarBtn(<BulletListIcon />, editor.isActive("bulletList"), () =>
                                editor.chain().focus().toggleBulletList().run()
                            )}
                            {toolbarBtn(<OrderedListIcon />, editor.isActive("orderedList"), () =>
                                editor.chain().focus().toggleOrderedList().run()
                            )}
                        </div>
                    )}
                    <div className={styles.editorBody}>
                        <EditorContent editor={editor} />
                    </div>
                </div>
            )}

            {/* View mode — always visible, shown in print */}
            {!editing &&
                (value || defaultContent ? (
                    <div
                        className={`audit-markdown-rendered ${styles.renderedContent}`}
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || defaultContent || "") }}
                    />
                ) : (
                    <p className={styles.emptyPlaceholder}>No content yet — click Edit to add notes.</p>
                ))}
        </div>
    );
}

export default AuditMarkdownField;
